import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useSelector } from 'react-redux';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { blue } from '@mui/material/colors';
import './style.css';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import WalletIcon from '@mui/icons-material/Wallet';
import SendIcon from '@mui/icons-material/Send';
import { OfferIcon  } from '../../util/icons';
import { dispatchErr, makeOffer, validNetwork } from '../../util/metamask';
import { useSnackbar } from 'notistack';
import { utils } from 'web3';
import Config from '../../config';

const MakeAnOffer = ({ open, metadata, onClose }) => {

    const labels = useMemo( () => new LocalizedStrings( LOCALIZATIONS ), []),
    [ loading, setLoading ] = useState( false ),
    { locale } = useSelector( state => state.app ),
    { accounts, network } = useSelector( state => state.eth ),
    [ offer, setOffer ] = useState( '' ),
    [ balance, setBalance ] = useState( 0 ),
    [ total, setTotal ] = useState( 0 ),
    [ invalidNetwork, setInvalidNetwork ] = useState( false ),
    { enqueueSnackbar } = useSnackbar(),
    bogusOffer = ( offer.length > 0 && balance < total );

    /**
     * @name getBalance
     * @function
     * @description Retrieves the selected Address's balance and sets it to `balance`
     */
    const getBalance = useCallback( async () => {

        const { selectedAddress } = window.ethereum,
        weiBalance = await window.ethereum.request({ 
            method: 'eth_getBalance',
            params: [
                selectedAddress,
                'latest'
            ]
        });

        setBalance( utils.fromWei( weiBalance, "ether" ) );

    }, [ setBalance ]);

    /**
     * @name onChange
     * @function
     * @description updates the offer amount
     * @param {HTMLElement} target 
     */
    const onChange = ({ target }) => {
        const { value } = target;
        if( !isNaN( value ) ){
            setOffer( value );
        }
    }

    /**
     * @name sendOffer
     * @function
     * @description Sends the offer to buy the NFT
     */
    const sendOffer = () => {
        setLoading( true );

        const { id, name } = metadata,
        newOffer = new Promise( makeOffer( id, utils.toWei( offer, "ether" ) ) );
        newOffer
            .then( () => {
                setLoading( false );
                enqueueSnackbar(
                    labels.formatString(
                        labels.success,
                        name
                    ),
                    { variant: 'success' }
                )
                onClose();
            })
            .catch( err => {
                console.error( 'error', err );
                setLoading( false );
                dispatchErr( err, enqueueSnackbar, labels );
            });
    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale );
    }, [ labels, locale ]);

    // make sure they're using the correct network
    useEffect(() => {
        if( open ){
            let valid = validNetwork( network );
            setInvalidNetwork( !valid, process.env.NODE_ENV === 'production' );
        }
    }, [ open, network, setInvalidNetwork ]);

    // get balance when the modal is open and the accounts change
    useEffect(() => {
        if( open ) getBalance();
    }, [ open, accounts, getBalance ]);

    useEffect(() => {
        const wei = utils.toWei( Config.contract.gasPrice.toString(), "gwei" );
        setTotal( Number( offer ) + Number( utils.fromWei( wei, "ether" ) ) );
    }, [ offer, setTotal ]);

    return (
        <Dialog open={ open } onClose={ onClose }>
            <DialogTitle>
                { 
                    labels.formatString(
                        labels.title,
                        metadata?.name || ''
                    )
                }
            </DialogTitle>
            <DialogContent>
                <Stack>                    
                    <List>
                        <ListItem disablePadding>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: blue[ 500 ] }}>
                                    <WalletIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={ labels.account } secondary={ accounts[ 0 ] } />
                        </ListItem>
                        {
                            invalidNetwork ?
                                <ListItem>
                                    <FormHelperText error>
                                        {
                                            process.env.NODE_ENV === 'production' ?
                                                labels.wrongNetwork :
                                                labels.goerliOnly
                                        }
                                    </FormHelperText>
                                </ListItem> :
                                null
                        }
                        <ListItem disablePadding>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: blue[ 500 ] }}>
                                    <OfferIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <FormControl fullWidth>
                                <TextField
                                    error={ bogusOffer }
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">ETH</InputAdornment>
                                    }}
                                    label={ labels.offerAmount }
                                    helperText={ bogusOffer ? labels.bogusOffer : null }
                                    name="name"
                                    onChange={ onChange } 
                                    placeholder="0.00"
                                    value={ offer } />
                            </FormControl>
                        </ListItem>
                    </List>
                    <Alert severity="info">
                        { 
                            labels.formatString(
                                labels.howBody,
                                labels.sendOffer
                            )
                        }
                    </Alert>
                </Stack>
                
            </DialogContent>
            <DialogActions>
                <Button aria-label={ labels.sendOffer } disabled={ bogusOffer || loading || offer.length === 0 } onClick={ sendOffer } variant="contained" startIcon={ 
                    loading ?
                        <CircularProgress size="1em" /> : 
                        <SendIcon /> 
                    }>
                    { labels.sendOffer }
                </Button>
                <Button aria-label={ labels.cancel } disabled={ loading } variant="outlined" onClick={ onClose } startIcon={ <NotInterestedIcon /> }>
                    { labels.cancel }
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default MakeAnOffer;